import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { API } from '@shared/types/api';
import { useListUsers } from '@shared/util/hooks/useListUsers';
import { MENTION_ITEM_LIMIT } from '../components/constants';

// Debounce non-empty @mention queries so we don't fire a request per keystroke
// (only used for the server-side path; client-side filtering is instant).
const MENTION_QUERY_DEBOUNCE_MS = 200;

interface Props {
  channelId?: number;
  // The current @mention query (text after `@`). `null` when the cursor is
  // not inside a mention; an empty string when the user has typed only `@`.
  query: string | null;
}

interface ChannelMentionUsers {
  users: API.ShortUserFragment[];
  hasMore: boolean;
  loading: boolean;
}

/**
 * Fetches the channel roster page that backs the @mention menu.
 *
 * The bare-`@` page (first `MENTION_ITEM_LIMIT` members, no username filter) is
 * fetched eagerly once the channel is known, so the menu is ready the instant the
 * user types `@`. When the whole roster fits in that page it is narrowed in-memory
 * as the user types (instant, like the original client-side filter). Larger rosters
 * fall back to a debounced, server-side `username` prefix filter that keeps showing
 * the previous results while the next page loads, so the list narrows in place
 * rather than blanking. This is a single paginated page — never the full roster.
 */
export function useChannelMentionUsers({ channelId, query }: Props): ChannelMentionUsers {
  const inMention = !isNil(channelId) && !isNil(query);
  const trimmedQuery = query?.trim() ?? '';
  const memberOfChannelIds = isNil(channelId) ? undefined : [channelId];
  const orderBys = [{ orderBy: 'firstName' as const, direction: 'ASC' as const }];

  // Base page: the channel roster with no username filter. Fetched eagerly (as
  // soon as the channel is known) and reused across keystrokes via the cache.
  const base = useListUsers({
    input: { memberOfChannelIds, limit: MENTION_ITEM_LIMIT, excludeSystemUsers: true, orderBys },
    skip: isNil(channelId),
  });

  // If the whole roster fits in the first page we can narrow it in-memory;
  // otherwise we filter server-side by username prefix.
  const canFilterClientSide = !base.hasMore;

  const [debouncedQuery] = useDebounce(trimmedQuery, MENTION_QUERY_DEBOUNCE_MS);
  const server = useListUsers({
    input: {
      memberOfChannelIds,
      limit: MENTION_ITEM_LIMIT,
      excludeSystemUsers: true,
      orderBys,
      username: debouncedQuery,
    },
    skip: !inMention || canFilterClientSide || isEmpty(debouncedQuery),
    keepPreviousData: true,
  });

  return useMemo(() => {
    // Empty query (`@`): just the base page.
    if (isEmpty(trimmedQuery)) {
      return { users: base.users, hasMore: base.hasMore, loading: base.loading };
    }
    // Small roster: filter client-side (case-insensitive substring match)
    if (canFilterClientSide) {
      const lowered = trimmedQuery.toLowerCase();
      const users = base.users.filter(user => user.username.toLowerCase().includes(lowered));
      return { users, hasMore: false, loading: base.loading };
    }
    // Large roster: server-filtered page (previous results retained while loading).
    return { users: server.users, hasMore: server.hasMore, loading: server.loading };
  }, [
    trimmedQuery,
    canFilterClientSide,
    base.users,
    base.hasMore,
    base.loading,
    server.users,
    server.hasMore,
    server.loading,
  ]);
}
